/**
 * Lot 2.3 — Veille réglementaire : l'administrateur enregistre ce qui change (source, conséquence, action), les
 * formateurs le lisent dans leur mode d'emploi. Indicateur Qualiopi 23 (et 24, 25 selon le thème).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ExternalLink, Newspaper, Pencil, Plus, RotateCcw } from "lucide-react";
import { api, dateFr, ErreurApi, type LigneVeille } from "../api";
import { Alerte, Bouton, Carte, Champ, Chargement, EtatVide, Etiquette, Modale, Onglets, Selecteur, TitrePage, ZoneTexte, useNotifier } from "../ui/base";

export const THEMES: Record<LigneVeille["theme"], string> = { legale: "Légale et réglementaire · ind. 23", metiers: "Compétences et métiers · ind. 24", pedagogie: "Pédagogie et technologies · ind. 25" };

export function AdminVeille() {
  const [onglet, setOnglet] = useState<"actives" | "archives">("actives");
  const [edition, setEdition] = useState<LigneVeille | "nouvelle" | null>(null);
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const liste = useQuery({ queryKey: ["veille", onglet], queryFn: () => api.get<LigneVeille[]>(`/veille${onglet === "archives" ? "?archives=1" : ""}`) });
  const archiver = useMutation({
    mutationFn: ({ id, archiver }: { id: string; archiver: boolean }) => api.post(`/admin/veille/${id}/archiver`, { archiver }),
    onSuccess: async (_r, v) => {
      await requetes.invalidateQueries({ queryKey: ["veille"] });
      notifier("succes", v.archiver ? "Information archivée." : "Information restaurée.");
    },
    onError: (e) => notifier("danger", e.message),
  });

  return (
    <>
      <TitrePage
        titre="Veille réglementaire"
        soustitre="Ce qui change dans la formation professionnelle, sa source et ce que cela implique pour l'organisme. Les informations diffusées apparaissent dans le mode d'emploi des formateurs."
        actions={
          <Bouton variante="signal" icone={<Plus className="size-4" aria-hidden />} onClick={() => setEdition("nouvelle")}>
            Nouvelle information
          </Bouton>
        }
      />
      <Onglets actif={onglet} choisir={setOnglet} onglets={[{ cle: "actives", libelle: "En cours" }, { cle: "archives", libelle: "Archivées" }]} />
      {liste.isPending ? (
        <Chargement />
      ) : liste.error ? (
        <Alerte ton="danger">{liste.error.message}</Alerte>
      ) : liste.data.length === 0 ? (
        <EtatVide icone={<Newspaper className="size-8" aria-hidden />} titre={onglet === "archives" ? "Aucune information archivée" : "Aucune information de veille"}>
          Qualiopi (indicateur 23) attend une veille légale et réglementaire, exploitée et diffusée. Notez ici chaque évolution, avec sa source officielle.
        </EtatVide>
      ) : (
        <ul className="space-y-3">
          {liste.data.map((l) => (
            <li key={l.id}>
              <FicheVeille l={l}>
                {onglet === "actives" ? (
                  <>
                    <Bouton taille="sm" variante="discret" icone={<Pencil className="size-3.5" aria-hidden />} onClick={() => setEdition(l)}>
                      Modifier
                    </Bouton>
                    <Bouton taille="sm" variante="discret" icone={<Archive className="size-3.5" aria-hidden />} onClick={() => archiver.mutate({ id: l.id, archiver: true })}>
                      Archiver
                    </Bouton>
                  </>
                ) : (
                  <Bouton taille="sm" variante="discret" icone={<RotateCcw className="size-3.5" aria-hidden />} onClick={() => archiver.mutate({ id: l.id, archiver: false })}>
                    Restaurer
                  </Bouton>
                )}
              </FicheVeille>
            </li>
          ))}
        </ul>
      )}
      {edition && <EditionVeille initiale={edition === "nouvelle" ? null : edition} fermer={() => setEdition(null)} />}
    </>
  );
}

/** Une information de veille, telle qu'elle se lit (admin et mode d'emploi du formateur). */
export function FicheVeille({ l, children }: { l: Pick<LigneVeille, "theme" | "titre" | "resume" | "impact" | "source_nom" | "source_url" | "date_information"> & { diffusee?: boolean }; children?: React.ReactNode }) {
  return (
    <Carte className="p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-encre-3">
        <Etiquette ton="accent">{THEMES[l.theme]}</Etiquette>
        {l.date_information && <span className="chiffres">{dateFr(l.date_information)}</span>}
        {l.diffusee === false && <Etiquette>Non diffusée aux formateurs</Etiquette>}
      </div>
      <h3 className="mt-2 text-[15px] font-semibold">{l.titre}</h3>
      {l.resume && <p className="mt-1 text-sm whitespace-pre-line text-encre-2">{l.resume}</p>}
      {l.impact && (
        <p className="mt-2 rounded-md bg-attente-doux/50 px-3 py-2 text-sm whitespace-pre-line">
          <strong>Pour nous : </strong>
          {l.impact}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        {l.source_url ? (
          <a href={l.source_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-[13px] font-medium text-accent hover:underline">
            {l.source_nom || "Source"} <ExternalLink className="size-3.5" aria-hidden />
            <span className="sr-only"> (nouvel onglet)</span>
          </a>
        ) : (
          <span className="text-[13px] text-encre-3">{l.source_nom}</span>
        )}
        {children && <div className="flex flex-wrap gap-1">{children}</div>}
      </div>
    </Carte>
  );
}

function EditionVeille({ initiale, fermer }: { initiale: LigneVeille | null; fermer: () => void }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const [v, setV] = useState({
    theme: initiale?.theme ?? ("legale" as LigneVeille["theme"]),
    titre: initiale?.titre ?? "",
    resume: initiale?.resume ?? "",
    impact: initiale?.impact ?? "",
    source_nom: initiale?.source_nom ?? "",
    source_url: initiale?.source_url ?? "",
    date_information: initiale?.date_information ?? "",
    diffusee: initiale?.diffusee ?? true,
  });
  const enregistrer = useMutation({
    mutationFn: () => (initiale ? api.patch(`/admin/veille/${initiale.id}`, v) : api.post("/admin/veille", v)),
    onSuccess: async () => {
      await requetes.invalidateQueries({ queryKey: ["veille"] });
      notifier("succes", "Information enregistrée.");
      fermer();
    },
  });
  const err = enregistrer.error instanceof ErreurApi ? (enregistrer.error.details?.champs ?? {}) : {};
  const champ = (cle: "titre" | "resume" | "impact" | "source_nom" | "source_url" | "date_information") => ({ value: v[cle], erreur: err[cle], onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV({ ...v, [cle]: e.target.value }) });
  return (
    <Modale ouverte fermer={fermer} titre={initiale ? "Modifier l'information" : "Nouvelle information de veille"} large>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); enregistrer.mutate(); }}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Selecteur libelle="Thème" value={v.theme} onChange={(e) => setV({ ...v, theme: e.target.value as LigneVeille["theme"] })}>
            {Object.entries(THEMES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Selecteur>
          <Champ libelle="Date de l'information" type="date" aide="Publication ou entrée en vigueur." {...champ("date_information")} />
        </div>
        <Champ libelle="Titre" required {...champ("titre")} />
        <ZoneTexte libelle="Ce qui change" rows={3} {...champ("resume")} />
        <ZoneTexte libelle="Conséquence pour l'organisme et action à mener" rows={3} aide="Ce que l'auditeur vous demandera : comment l'information a été exploitée." {...champ("impact")} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
          <Champ libelle="Source" placeholder="Ex. Service-Public, France Compétences" {...champ("source_nom")} />
          <Champ libelle="Adresse de la source" type="url" placeholder="https://…" {...champ("source_url")} />
        </div>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-accent)]" checked={v.diffusee} onChange={(e) => setV({ ...v, diffusee: e.target.checked })} />
          <span>Diffuser aux formateurs (mode d'emploi)</span>
        </label>
        {enregistrer.error && Object.keys(err).length === 0 && <Alerte ton="danger">{enregistrer.error.message}</Alerte>}
        <div className="flex justify-end gap-2">
          <Bouton variante="discret" onClick={fermer}>Annuler</Bouton>
          <Bouton type="submit" variante="primaire" enCours={enregistrer.isPending}>Enregistrer</Bouton>
        </div>
      </form>
    </Modale>
  );
}
